import {
  ActivityItem,
  ActivityProduct,
  ActivityProductDistribution,
  ActivityProductOption,
  ActivityProductRestrictions,
  Archive,
  QuantityType,
  SubParticipantSwishOptions,
  SwishRecipient,
} from '@app/_store';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ActivityActions {
  export class Reset {
    static readonly type = '[ACTIVITY] Reset';
  }

  export class Create {
    static readonly type = '[ACTIVITY] Create';
    constructor(
      public form: {
        name: string;
        description: string;
        partialPaymentAllowed: boolean;
        smsPaymentReminderEnabled: boolean;
        subParticipantsAllowed: boolean;
        subParticipantSwishOption?: SubParticipantSwishOptions;
        templateMessage?: string;
        swish?: { number: string; name: string; default: boolean };
      },
      public type: string,
      public template: ActivityItem | string
    ) {}
  }

  export class Update {
    static readonly type = '[ACTIVITY] Update';
    constructor(
      public form: {
        name: string;
        description: string;
        deliveryInformation: string;
        responsible: { name: string; email: string };
        partialPaymentAllowed: boolean;
        smsPaymentReminderEnabled: boolean;
        subParticipantsAllowed: boolean;
        subParticipantSwishOption: SubParticipantSwishOptions;
        templateMessage: string;
        swish: SwishRecipient;
      }
    ) {}
  }

  export class Export {
    static readonly type = '[ACTIVITY] Export';
  }

  export class Import {
    static readonly type = '[ACTIVITY] Import';
    constructor(public data: FormData) {}
  }

  export class View {
    static readonly type = '[ACTIVITY] View';
    constructor(public id: string) {}
  }

  export class ClearCurrent {
    static readonly type = '[ACTIVITY] Clear Current';
  }

  export class LockProduct {
    static readonly type = '[ACTIVITY] Lock Product';
    constructor(public productId: string) {}
  }

  export class UnlockProduct {
    static readonly type = '[ACTIVITY] Unlock Product';
    constructor(public product: ActivityProduct) {}
  }

  export class Init {
    static readonly type = '[ACTIVITY] Init';
  }

  export class LoadTemplates {
    static readonly type = '[ACTIVITY] Load Templates';
  }

  export class ToggleTemplatePublished {
    static readonly type = '[ACTIVITY] Toggle Template Published';
  }

  export class CreateProduct {
    static readonly type = '[ACTIVITY] Create Product';
    constructor(
      public product: {
        title: string;
        description: string;
        amount: number;
        profit?: number;
        isUserSelectable: boolean;
        quantityType: QuantityType;
        defaultQuantity?: number;
        isDeliverable: boolean;
        requireComment: boolean;
        options: ActivityProductOption[];
        restrictions: ActivityProductRestrictions;
        costDistributions: ActivityProductDistribution[];
        profitDistributions: ActivityProductDistribution[];
      }
    ) {}
  }

  export class CopyProductsFromActivity {
    static readonly type = '[ACTIVITY] Copy Product From Activity';
    constructor(
      public fromActivityId: string,
      public isArchived: boolean
    ) {}
  }

  export class DeleteProduct {
    static readonly type = '[ACTIVITY] Delete Product';
    constructor(public product: ActivityProduct) {}
  }

  export class DeleteActivity {
    static readonly type = '[ACTIVITY] Delete Activity';
  }

  export class AddProductImage {
    static readonly type = '[ACTIVITY] Add Product Image';
    constructor(
      public productId: string,
      public file: File
    ) {}
  }

  export class DeleteProductImage {
    static readonly type = '[ACTIVITY] Delete Product Image';
    constructor(
      public productId: string,
      public imageId: string
    ) {}
  }

  export class ArchiveActivity {
    static readonly type = '[ACTIVITY] Archive Activity';
  }

  export class DownloadArchive {
    static readonly type = '[ACTIVITY] Download Archive';
    constructor(public archive: Archive) {}
  }

  export class DeleteArchive {
    static readonly type = '[ACTIVITY] Delete Archive';
    constructor(public archive: Archive) {}
  }

  export class SetActivityImage {
    static readonly type = '[ACTIVITY] Set Activity Image';
    constructor(public file: File) {}
  }

  export class DeleteActivityImage {
    static readonly type = '[ACTIVITY] Delete Activity Image';
  }

  export class UpdateProduct {
    static readonly type = '[ACTIVITY] Update Product';
    constructor(
      public productId: string,
      public product: {
        title: string;
        description: string;
        amount: number;
        profit?: number;
        isUserSelectable: boolean;
        quantityType: QuantityType;
        defaultQuantity?: number;
        isDeliverable: boolean;
        requireComment: boolean;
        options: ActivityProductOption[];
        restrictions: ActivityProductRestrictions;
        costDistributions: ActivityProductDistribution[];
        profitDistributions: ActivityProductDistribution[];
      },
      public updateParticipants?: string
    ) {}
  }

  export class MoveProduct {
    static readonly type = '[ACTIVITY] Move Product';
    constructor(
      public productId: string,
      public position: number
    ) {}
  }

  export class ChangeProductOrder {
    static readonly type = '[ACTIVITY] Change Product Order';
    constructor(
      public productId: string,
      public direction: 'up' | 'down'
    ) {}
  }

  export class CalculateSummary {
    static readonly type = '[ACTIVITY] CalculateSummary';
  }

  export class ActivateProductSelections {
    static readonly type = '[ACTIVITY] Activate Product Selections';
    constructor(
      public deadline: Date,
      public productSelectionReminderEnabled: boolean
    ) {}
  }

  export class ActivatePayments {
    static readonly type = '[ACTIVITY] Activate Payments';
    constructor(
      public deadline: Date,
      public paymentReminderEnabled: boolean
    ) {}
  }

  export class ActivateRegistration {
    static readonly type = '[ACTIVITY] Activate Registration';
    constructor(public deadline: Date) {}
  }

  export class UpdateCurrentActivityCountsInList {
    static readonly type = '[ACTIVITY] Update Current Activity Counts In List';
  }

  export class SearchPayment {
    static readonly type = '[ACTIVITY] Search Payment';
    constructor(public reference: string) {}
  }

  export class ClearSearchPaymentResults {
    static readonly type = '[ACTIVITY] Clear Search Payment Results';
  }

  export class DownloadActivityReport {
    static readonly type = '[ACTIVITY] Download Activity Report';
    constructor(
      public report: ReportType,
      public fileName: string
    ) {}
  }

  export class LoadArchives {
    static readonly type = '[ACTIVITY] Load Archives';
  }

  export class ResetArchives {
    static readonly type = '[ACTIVITY] Reset Archives';
  }

  export class UpdateRequired {
    static readonly type = '[ACTIVITY] Update Required';
    constructor(public activityId: string) {}
  }
}

export enum ReportType {
  ProductQuantity = 'product-quantity',
  Products = 'products',
  Summary = 'summary',
  DeliveryTicking = 'delivery-ticking',
  DeliveryLabels = 'delivery-labels',
  Distribution = 'distribution',
  ParticipantList = 'participant-list',
}

export enum EmailType {
  ParticipantActivityLink = 1,
  ActivityUpdated = 2,
  ProductSelectionReminder = 3,
  PaymentReminder = 4,
  AdministratorRequestApproved = 5,
  ParticipantRegistration = 6,
}
